import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AZMenuConfig, AZSportsDataModel, CalendarConfig, Sitecore, TimeFilterConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, ItemType, takeTop } from '@frontend/sports/common/core/data-access/sport-model';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { FreshDataService } from '@frontend/sports/data-refresh/feature';
import { ItemComponent } from '@frontend/sports/item/feature';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { AZSportsViewModel } from '@frontend/sports/types/components/a-z-menu';
import { DeviceService } from '@frontend/vanilla/core';
import { differenceBy, partition } from 'lodash-es';

import { CalendarItemListComponent } from '../calendar/calendar-item-list.component';
import { CalendarPage, CalendarService } from '../calendar/calendar.service';
import { IntervalLink } from '../calendar/count-interval.service';
import { TimeFrameLocation } from '../calendar/time-filters-tracking.service';
import { HeadingComponent } from '../common/heading.component';
import { SportTreeItem } from '../competition-list/competition-list.models';
import { SportTreeService } from '../competition-list/sport-tree.service';
import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { EventListService } from '../event-list/services/event-list.service';
import { SportListGroup, SportListModalService } from './services/sport-list-modal.service';
import { SportListComponent } from './sport-list.component';

@Component({
    selector: 'ms-sport-list-body',
    templateUrl: 'sport-list-body.component.html',
    imports: [ItemComponent, HeadingComponent, CalendarItemListComponent, SportListComponent],
})
export class SportListBodyComponent implements OnInit {
    private SPORTS_TOP_COUNT = 6;
    @Input() topGroup = false;
    @Input() isMobile = true;

    top: SportTreeItem[] = [];
    groups: SportListGroup[] = [];
    bottomNavigation: boolean;
    crmPromotedSportsMap: StringDictionary<number>;
    showNewBadge: boolean;
    calendarIntervals: IntervalLink[] = [];
    readonly timeFrameLocation = TimeFrameLocation.AzMenu;

    constructor(
        public sitecore: Sitecore,
        private sportMapper: SportTreeService,
        private eventService: EventListService,
        private listService: SportListModalService,
        private layoutService: AdaptiveLayoutService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private freshData: FreshDataService,
        private urlHelperService: UrlHelperService,
        private deviceService: DeviceService,
        private calendarService: CalendarService,
        private timeFilterConfig: TimeFilterConfig,
        private azMenuConfig: AZMenuConfig,
        private redirectHelper: RedirectHelperService,
        private calendarConfig: CalendarConfig,
        private azSportsData: AZSportsDataModel,
        private destroyRef: DestroyRef,
    ) {}

    ngOnInit(): void {
        this.showNewBadge = this.azMenuConfig.showNewBadge;
        this.freshData.reloadNeeded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.buildSportList());
        this.layoutService.stateChange$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((state) => (this.bottomNavigation = state.bottomNavigation));

        this.buildSportList();
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ids) => this.promotionMapChanged(ids));
        this.calendarIntervals = this.showCalendarTimeFilters ? this.calendarService.getCalendarLinks(CalendarPage.AtozSports) : [];
    }

    redirectToSearch(): void {
        this.redirectHelper.goToPage(this.urlHelperService.getSearchUrl(), true);
    }

    private buildSportList(): void {
        this.eventService.getSportList().subscribe((sports) => {
            const [withoutEsports, esports] = partition(sports, (sport) => !sport.isEsport);
            const [azWithoutEsports, azEsportsItems] = partition(
                this.azSportsData.azSports.filter((azSport) => !sports.find((item) => item.name === azSport.activeName)),
                (az) => !az.isEsportsLobby,
            );

            withoutEsports.push(...azWithoutEsports.map(this.mapAZViewModel));

            if (esports.length > 0) {
                const allowedAZMenuEsports = esports.filter((sport) => this.azMenuConfig.allowedEsportsOnAZMenu.includes(sport.id));
                withoutEsports.push(...allowedAZMenuEsports);
                withoutEsports.push(...azEsportsItems.map(this.mapAZViewModel));
            }

            const top = takeTop(withoutEsports, this.SPORTS_TOP_COUNT);

            const other = this.topGroup ? differenceBy(withoutEsports, top, (sport) => sport.id) : withoutEsports;

            this.top = this.sportMapper.get(top, true);
            if (this.isMobile && this.azMenuConfig.removeLetterBreakersInMobile) {
                this.groups = this.listService.getSingleGroup(this.sportMapper.get(other, true));
            } else {
                this.groups = this.listService.getGroups(this.sportMapper.get(other, true));
            }
        });
    }

    private promotionMapChanged(promoMap: PromotionMap): void {
        this.crmPromotedSportsMap = promoMap.sports;
    }

    get isMobileLayout(): boolean {
        return this.deviceService.isMobile;
    }

    get showCalendarTimeFilters(): boolean {
        return this.timeFilterConfig.isTimeFilterEnabled && this.calendarConfig.showCalendarTab;
    }

    private mapAZViewModel(azsport: AZSportsViewModel): CountItem {
        return {
            name: azsport.activeName,
            id: azsport.isEsportsLobby ? SportConstant.EsportsGroup : Number(azsport.id) || 0,
            type: ItemType.Sport,
            icon: azsport.icon,
            counts: {
                live: 0,
                preMatch: 0,
                gridable: 0,
                nonGridable: 0,
                outrights: 0,
                specials: 0,
                other: 0,
                antepost: 0,
                liveStreaming: 0,
                outrightsByGroups: {},
                specialsByGroups: {},
            },
            meta: {
                calendar: false,
                coupons: false,
                teamPages: false,
                calendarIntervals: [],
            },
            children: [],
        };
    }
}
